import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { keyed } from 'lit/directives/keyed.js';
import { tokens } from './styles/tokens';
import { shared } from './styles/shared';

import { DIALOG } from './data/dialog';
import { RECAP } from './data/recap';
import { DEPARTEMENTS } from './data/departements';
import { ARTICLES_PRO } from './data/articles-pro';
import { STRINGS } from './data/strings';

import { CookieStore } from './services/cookie-store';
import { DialogEngine } from './services/dialog-engine';
import { ApiClient } from './services/api-client';

import type {
  DialogNode,
  FormContent,
  RecapArticle,
  RecapEntry,
  RecapSituationLine,
  SarahMode,
  SarahPrefill,
  SarahRecapApi,
  UserChoiceValue,
  ApiOverrides,
} from './types';

import './components/sarah-speaker';
import './components/sarah-choix';
import './components/sarah-formulaire';
import './components/sarah-bouton';
import './components/sarah-waiting';
import './components/sarah-recap';
import './components/sarah-inscription';

declare const __BUILD_DATE__: string;
declare const __GIT_SHA__: string;

/**
 * Lit utilise un mécanisme de "scoped" CSS variable. Pour que les composants
 * récap puissent partager le scope `--sarah-*` de sarah-bot, on les inclut
 * tous dans le même Shadow DOM (via les imports ci-dessus). Les CSS custom
 * properties traversent les boundaries de Shadow DOM, donc l'override depuis
 * l'app hôte fonctionne quand même.
 */

/**
 * Les @font-face dans Shadow DOM ne sont pas appliqués au document parent (et
 * Lit n'a pas de mécanisme natif pour les déclarer globalement). On les injecte
 * une seule fois dans <head> pour que Nunito/Lato soient disponibles partout.
 */
const FONT_STYLE_ID = 'sarah-lit-fonts';
function injectGlobalFonts(assetsBase: string): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(FONT_STYLE_ID)) return;
  const base = assetsBase.replace(/\/$/, '');
  const style = document.createElement('style');
  style.id = FONT_STYLE_ID;
  style.textContent = `
@font-face { font-family: 'Nunito'; font-weight: 400; src: local('Nunito Regular'), local('Nunito-Regular'), url('${base}/fonts/Nunito-Regular.ttf'); font-display: swap; }
@font-face { font-family: 'Nunito'; font-weight: 600; src: local('Nunito SemiBold'), local('Nunito-SemiBold'), url('${base}/fonts/Nunito-SemiBold.ttf'); font-display: swap; }
@font-face { font-family: 'Nunito'; font-weight: 700; src: local('Nunito Bold'), local('Nunito-Bold'), url('${base}/fonts/Nunito-Bold.ttf'); font-display: swap; }
@font-face { font-family: 'Nunito'; font-weight: 800; src: local('Nunito ExtraBold'), local('Nunito-ExtraBold'), url('${base}/fonts/Nunito-ExtraBold.ttf'); font-display: swap; }
@font-face { font-family: 'Lato'; font-weight: 400; src: local('Lato Regular'), local('Lato-Regular'), url('${base}/fonts/Lato-Regular.ttf'); font-display: swap; }
@font-face { font-family: 'Lato'; font-weight: 700; src: local('Lato Bold'), local('Lato-Bold'), url('${base}/fonts/Lato-Bold.ttf'); font-display: swap; }
@font-face { font-family: 'Lato'; font-weight: 900; src: local('Lato Black'), local('Lato-Black'), url('${base}/fonts/Lato-Black.ttf'); font-display: swap; }
`;
  document.head.appendChild(style);
}

const HASH_TO_NODE: Record<string, string> = {
  suspension: 'ask-suspension',
  annulation: 'ask-lettre-4',
  invalidation: 'ask-lettre-3',
};

const BONJOUR_NODES = new Set(['ask-lettre-4', 'ask-lettre-3', 'ask-suspension']);

const WAITING_MIN_MS = 5000;
const REDIRECT_AFTER_LOGIN_MS = 3000;

@customElement('sarah-bot')
export class SarahBot extends LitElement {
  static styles = [
    tokens,
    shared,
    css`
      :host {
        display: block;
        font-family: var(--sarah-font-body);
        color: var(--sarah-text);
      }
      #sarah {
        max-width: var(--sarah-max-width);
        margin-left: auto;
        margin-right: auto;
        padding: 38px 20px 20px;
      }
      @media (max-width: 720px) {
        #sarah { padding: 38px 16px; }
      }
      #sarah.sarah--final { max-width: 1140px; }

      .sarah-container, .sarah-waiting-wrap, .sarah-final-wrap {
        animation: fadeSlideIn .3s ease;
      }
      @keyframes fadeSlideIn {
        from { opacity: 0; transform: translateY(30px); }
        to   { opacity: 1; transform: translateY(0); }
      }

      .speaker-bonjour {
        background: #f3f6fc;
        border-left: 4px solid var(--sarah-bleu);
        padding: 12px 16px;
        border-radius: 8px;
        margin-bottom: 16px;
      }

      .courrier-exemple {
        display: block;
        max-width: 100%;
        margin: 16px 0;
        border: 1px solid #ebe9e3;
        border-radius: 8px;
      }

      .reveal-wrap.before-typing { min-height: 80px; }
      .reveal-wrap.after-typing { animation: revealFade .4s ease; }
      .reveal { animation: revealFade .4s ease; }
      @keyframes revealFade {
        from { opacity: 0; transform: translateY(8px); }
        to   { opacity: 1; transform: translateY(0); }
      }

      .modal-overlay {
        position: fixed;
        inset: 0;
        background: rgba(0,0,0,.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        padding: 16px;
      }
      .modal-overlay .modal-inner {
        max-width: 600px;
        width: 100%;
        position: relative;
      }
      .modal-close {
        position: absolute;
        top: 8px;
        right: 8px;
        background: transparent;
        border: none;
        font-size: 28px;
        line-height: 1;
        cursor: pointer;
        color: #888;
        z-index: 2;
      }

      .pro-terminal {
        background: #fff;
        padding: 35px;
        border-radius: 20px;
        box-shadow: 0 1px 11px 2px #0000001a, 0 1px 2px 0 #0000000f;
      }
      .pro-terminal h2 {
        margin-top: 0;
        font-family: var(--sarah-font-display);
        font-size: 24px;
        color: var(--sarah-bleu);
      }
      .pro-terminal .articles { display: flex; flex-direction: column; gap: 8px; }
      .pro-terminal .articles a {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 12px 14px;
        background: #f5f3ed;
        border-radius: 8px;
        text-decoration: none;
        color: var(--sarah-bleu);
        font-weight: 600;
        transition: background-color .2s;
      }
      .pro-terminal .articles a:hover { background: #ece8db; }
      .pro-terminal .goto-pro { text-align: center; margin-top: 20px; }
      .pro-terminal .goto-pro a {
        display: inline-block;
        padding: 12px 24px;
        background: var(--sarah-bleu);
        color: #fff;
        border-radius: var(--sarah-radius-btn);
        text-decoration: none;
        font-weight: 700;
      }
      .pro-terminal .goto-pro a:hover { background: var(--sarah-bleu-hover); }
      .pro-terminal .search-form {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
        margin: 24px 0;
        align-items: flex-end;
      }
      .pro-terminal .search-form select {
        flex: 1 1 220px;
        padding: 12px 14px;
        font-size: 16px;
        border: 1px solid var(--sarah-border);
        border-radius: 6px;
        background: #fff;
      }
    `,
  ];

  // ---------- Attributes / properties ----------

  @property({ type: String, attribute: 'api-base' }) apiBase = '/api/sarah';
  @property({ type: String }) mode: SarahMode = 'signup';
  @property({ type: String, attribute: 'start-key' }) startKey = '';
  @property({ type: Boolean, attribute: 'hash-routing' }) hashRouting = false;
  @property({ type: String, attribute: 'app-url' }) appUrl = 'https://app.aaaep.fr/';
  @property({ type: String, attribute: 'assets-base' }) assetsBase = '';

  @property({ type: Object, attribute: false }) prefill: SarahPrefill = {};
  @property({ type: Object, attribute: false }) apiOverrides: ApiOverrides = {};

  // ---------- Internal state ----------

  @state() private currentData: DialogNode | null = null;
  @state() private savedCurrentData: UserChoiceValue | '' = '';
  @state() private currentFormContent: FormContent | null = null;
  @state() private isBtnDisabled = true;
  @state() private waiting = false;
  @state() private isFinal = false;
  @state() private existsEmail = false;
  @state() private finalRecap: SarahRecapApi | null = null;
  @state() private inscriptionConnected = false;
  @state() private inscriptionError = '';
  @state() private modalOpen = false;
  @state() private proTerminalKey: string | null = null;
  @state() private speakerDone = false;
  @state() private btnLoading = false;

  // ---------- Services (initialisés au connectedCallback) ----------

  private cookies!: CookieStore;
  private engine!: DialogEngine;
  private api!: ApiClient;

  private staticFrom: 'site' | null = null;
  private isFromInternal: { is: string; key: string | null } = { is: 'first', key: null };
  private waitingMinPromise: Promise<void> | null = null;
  private bonjourShouldShow = false;
  private modalOpener: HTMLElement | null = null;

  // ---------- Lifecycle ----------

  connectedCallback(): void {
    super.connectedCallback();
    this.cookies = new CookieStore();
    this.engine = new DialogEngine(DIALOG, this.cookies);
    this.api = new ApiClient({
      baseUrl: this.apiBase,
      overrides: this.apiOverrides,
    });

    if (!this.assetsBase) this.assetsBase = this.autoDetectAssetsBase();
    injectGlobalFonts(this.assetsBase);

    // Reset systématique des cookies de session (user_choices, user_map,
    // user_visitor) à chaque arrivée sur Sarah. Évite les bugs de choix
    // hérités d'un parcours abandonné (ex : prefill périmé, branche
    // incohérente avec la situation courante du candidat).
    this.engine.reset();

    this.applyPrefill();
    this.bootstrap();
    // eslint-disable-next-line no-console
    console.log(`[sarah-bot] build ${__GIT_SHA__} (${__BUILD_DATE__}) — mode=${this.api.mode}`);
  }

  updated(changed: Map<string | number | symbol, unknown>): void {
    if (changed.has('modalOpen') && this.modalOpen) {
      requestAnimationFrame(() => this.focusFirstModalInput());
    }
    // Angular set apiOverrides via property dans ngAfterViewInit, donc APRÈS
    // notre connectedCallback. On réinitialise l'ApiClient pour qu'il prenne
    // en compte ces nouveaux overrides (et donc utilise l'HttpClient Angular).
    if ((changed.has('apiOverrides') || changed.has('apiBase')) && this.api) {
      this.api = new ApiClient({
        baseUrl: this.apiBase,
        overrides: this.apiOverrides,
      });
    }
  }

  private focusFirstModalInput(): void {
    const root = this.shadowRoot;
    if (!root) return;
    try {
      const inscription = root.querySelector('sarah-inscription') as HTMLElement | null;
      if (inscription && inscription.shadowRoot) {
        const emailInput = inscription.shadowRoot.querySelector(
          'input[name="formEmail"]',
        ) as HTMLElement | null;
        if (emailInput) { emailInput.focus(); return; }
        const anyInput = inscription.shadowRoot.querySelector('input, select') as HTMLElement | null;
        if (anyInput) { anyInput.focus(); return; }
      }
      const closeBtn = root.querySelector('.modal-close') as HTMLElement | null;
      if (closeBtn) closeBtn.focus();
    } catch (_) {
      /* sous-composant pas prêt */
    }
  }

  /**
   * Détecte le dossier d'hébergement du bundle (les assets sont au même endroit,
   * conformément au build Vite par défaut qui copie `public/` à la racine de
   * `dist/`). Fallback `.` (chemin relatif au document).
   */
  private autoDetectAssetsBase(): string {
    try {
      const scripts = Array.from(document.querySelectorAll('script[src]')) as HTMLScriptElement[];
      const me = scripts.find((s) => /sarah-bot(\.\w+)?\.js/.test(s.src));
      if (me) {
        const url = new URL(me.src, window.location.href);
        return url.pathname.replace(/\/sarah-bot[^/]*\.js$/, '');
      }
    } catch (_) { /* fallback */ }
    return '.';
  }

  private applyPrefill(): void {
    if (!this.prefill || (!this.prefill.nom && !this.prefill.prenom && !this.prefill.email)) {
      return;
    }
    // On injecte directement le contenu form-informations dans user_choices
    // pour que `getLastValue('ask-informations')` retrouve les valeurs.
    const choices = this.cookies.getJSON<Record<string, UserChoiceValue>>('user_choices', {});
    const formValue: FormContent = {
      key: 'form-informations',
      value: {
        formNom: this.prefill.nom ?? '',
        formPrenom: this.prefill.prenom ?? '',
        formEmail: this.prefill.email ?? '',
      },
    };
    choices['ask-informations'] = formValue;
    this.cookies.setJSON('user_choices', choices);
    if (this.prefill.prenom) {
      this.cookies.set('user_visitor', JSON.stringify(this.prefill.prenom));
    }
  }

  private bootstrap(): void {
    let initial: DialogNode | null = null;
    const hash = (this.hashRouting && location.hash) ? location.hash.replace('#', '') : '';

    if (hash && HASH_TO_NODE[hash]) {
      this.staticFrom = 'site';
      this.isFromInternal = { is: 'site', key: hash };
      this.engine.action = { is: 'back', key: HASH_TO_NODE[hash] };
      initial = this.engine.getMessage();
    } else {
      const key = this.startKey || (this.mode === 'onboarding' ? 'retrait-permis' : 'accept');
      this.engine.action = { is: 'first', key };
      initial = this.engine.getMessage();
    }

    if (!initial) {
      console.error('[sarah-bot] no initial node found');
      return;
    }

    this.applyNode(initial);
  }

  private applyNode(node: DialogNode): void {
    this.currentData = node;
    this.bonjourShouldShow = this.computeBonjour();
    this.speakerDone = false;
    const last = this.engine.getLastValue(node.cle);
    this.savedCurrentData = last.data;
    this.currentFormContent =
      last.data && typeof last.data === 'object' ? (last.data as FormContent) : null;
    this.isBtnDisabled = !this.savedCurrentData;
    this.emitBackState();
    this.emitStateChange();
  }

  private onTypingDone = (): void => {
    this.speakerDone = true;
    // Le focus suit la conversation : on attend la fin du typing pour ne pas
    // interrompre la lecture du message par un screen reader.
    requestAnimationFrame(() => this.focusFirstInteractive());
  };

  private focusFirstInteractive(): void {
    const root = this.shadowRoot;
    if (!root) return;
    try {
      const choix = root.querySelector('sarah-choix') as HTMLElement | null;
      if (choix && choix.shadowRoot) {
        const choice = choix.shadowRoot.querySelector(
          '.choice[role="button"]',
        ) as HTMLElement | null;
        if (choice) {
          choice.focus();
          return;
        }
      }
      const form = root.querySelector('sarah-formulaire') as HTMLElement | null;
      if (form && form.shadowRoot) {
        const field = form.shadowRoot.querySelector(
          'input, select',
        ) as HTMLElement | null;
        if (field) {
          field.focus();
          return;
        }
      }
      const bouton = root.querySelector('sarah-bouton') as HTMLElement | null;
      if (bouton && bouton.shadowRoot) {
        const btn = bouton.shadowRoot.querySelector('button.btn') as HTMLElement | null;
        if (btn) {
          btn.focus();
          return;
        }
      }
    } catch (_) {
      /* shadow root pas accessible (mode closed) ou sous-composant pas encore upgradé */
    }
  }

  private computeBonjour(): boolean {
    if (!this.currentData) return false;
    const cle = this.currentData.cle;
    const isFrom = this.isFromInternal;
    if (isFrom.is === 'site' && BONJOUR_NODES.has(cle)) return true;
    if (this.staticFrom === 'site' && isFrom.is === 'back' && BONJOUR_NODES.has(cle)) return true;
    return false;
  }

  // ---------- Events publics ----------

  private emit(name: string, detail: unknown): void {
    this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true }));
  }

  private emitBackState(): void {
    const map = this.cookies.getJSON<string[]>('user_map', []);
    this.emit('sarah:back-state', { canGoBack: map.length > 0 });
  }

  private emitStateChange(): void {
    if (!this.currentData) return;
    const map = this.cookies.getJSON<string[]>('user_map', []);
    this.emit('sarah:state-change', {
      node: this.currentData.cle,
      type: this.currentData.type,
      depth: map.length,
      mode: this.mode,
    });
  }

  // ---------- Méthodes publiques ----------

  goBack(): boolean {
    const previous = this.engine.getIndexBefore();
    if (!previous) {
      this.emit('sarah:exit', { reason: 'home' });
      return false;
    }
    this.engine.action = { is: 'back', key: previous };
    this.isFromInternal = this.engine.action;
    this.isFinal = false;
    this.waiting = false;
    this.proTerminalKey = null;
    const next = this.engine.getMessage();
    if (!next) {
      this.emit('sarah:exit', { reason: 'home' });
      return false;
    }
    this.applyNode(next);
    return true;
  }

  reset(): void {
    this.engine.reset();
    this.currentData = null;
    this.savedCurrentData = '';
    this.currentFormContent = null;
    this.isBtnDisabled = true;
    this.waiting = false;
    this.isFinal = false;
    this.existsEmail = false;
    this.finalRecap = null;
    this.inscriptionConnected = false;
    this.inscriptionError = '';
    this.modalOpen = false;
    this.proTerminalKey = null;
    this.bootstrap();
  }

  setNode(cle: string): void {
    const node = DIALOG.find((n) => n.cle === cle);
    if (!node) return;
    this.engine.action = { is: 'back', key: cle };
    this.isFromInternal = this.engine.action;
    const next = this.engine.getMessage();
    if (next) this.applyNode(next);
  }

  // ---------- Handlers internes ----------

  private onChoice = (e: Event): void => {
    if (this.btnLoading) return;
    const cle = (e as CustomEvent).detail.cle as string;
    console.log('[sarah-bot] choice clicked:', cle);
    if (cle === 'ignore') {
      this.emit('sarah:exit', { reason: 'home' });
      return;
    }
    this.savedCurrentData = cle;
    void this.handleAction({ is: 'other', key: cle });
  };

  private onNext = (): void => {
    if (this.btnLoading) return;
    const key = this.currentFormContent ? this.currentFormContent.key : (this.savedCurrentData as string);
    void this.handleAction({ is: 'button', key });
  };

  private onFormValidChange = (e: Event): void => {
    const detail = (e as CustomEvent).detail as { valid: boolean; data: FormContent };
    this.currentFormContent = detail.data;
    this.isBtnDisabled = !detail.valid;
  };

  private onOpenLogin = (e: Event): void => {
    // Mémorise l'élément déclencheur pour pouvoir y restaurer le focus à la fermeture.
    const path: EventTarget[] = typeof e.composedPath === 'function' ? e.composedPath() : [];
    const fromPath = path.find((n): n is HTMLElement => n instanceof HTMLElement);
    const target = e.target instanceof HTMLElement ? e.target : null;
    this.modalOpener = fromPath ?? target;
    this.modalOpen = true;
  };

  private closeModal(): void {
    if (!this.modalOpen) return;
    this.modalOpen = false;
    const opener = this.modalOpener;
    this.modalOpener = null;
    if (opener) {
      requestAnimationFrame(() => {
        try { opener.focus(); } catch (_) { /* opener détaché */ }
      });
    }
  }

  private onModalKeydown = (e: KeyboardEvent): void => {
    if (!this.modalOpen) return;
    if (e.key === 'Escape') {
      e.stopPropagation();
      this.closeModal();
      return;
    }
    if (e.key !== 'Tab') return;
    const focusables = this.getModalFocusables();
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = this.getActiveFocusable();
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  private getModalFocusables(): HTMLElement[] {
    const root = this.shadowRoot;
    if (!root) return [];
    const inner = root.querySelector('.modal-inner');
    if (!inner) return [];
    const list: HTMLElement[] = [];
    const closeBtn = inner.querySelector('.modal-close') as HTMLElement | null;
    if (closeBtn) list.push(closeBtn);
    const inscription = inner.querySelector('sarah-inscription') as HTMLElement | null;
    if (inscription && inscription.shadowRoot) {
      const inner2 = inscription.shadowRoot.querySelectorAll<HTMLElement>(
        'input, select, textarea, button, a[href], [tabindex]:not([tabindex="-1"])',
      );
      inner2.forEach((el) => {
        if (!el.hasAttribute('disabled')) list.push(el);
      });
    }
    return list;
  }

  private getActiveFocusable(): HTMLElement | null {
    let active: Element | null = document.activeElement;
    while (active && active.shadowRoot && active.shadowRoot.activeElement) {
      active = active.shadowRoot.activeElement;
    }
    return active instanceof HTMLElement ? active : null;
  }

  private onLoginSubmit = (e: Event): void => {
    const detail = (e as CustomEvent).detail as { email: string; password: string };
    void this.doLogin(detail.email, detail.password);
  };

  // ---------- Action handler (cœur du moteur) ----------

  private async handleAction(action: { is: string; key: string }): Promise<void> {
    const previousCle = this.currentData ? this.currentData.cle : '';
    const formContent = this.currentFormContent;
    console.log('[sarah-bot] handleAction', action, 'from', previousCle, 'formContent=', formContent);

    // checkMail avant transition vers final
    if (
      action.is === 'button' &&
      formContent &&
      typeof formContent.value === 'object' &&
      (formContent.value as Record<string, unknown>).formEmail
    ) {
      const email = String((formContent.value as Record<string, unknown>).formEmail);
      this.btnLoading = true;
      let exists = false;
      try {
        exists = await this.api.checkMail(email);
      } finally {
        this.btnLoading = false;
      }
      this.existsEmail = exists;
      if (exists) return;
    }

    this.engine.action = { is: action.is as 'first' | 'other' | 'button' | 'back', key: action.key };
    this.isFromInternal = this.engine.action;
    this.waiting = false;

    let next = this.engine.getMessage(previousCle, formContent);
    console.log('[sarah-bot] → next node:', next ? next.cle : null, next ? next.type : null);
    if (!next) {
      console.warn('[sarah-bot] dead-end — no node with pere=' + action.key);
      return;
    }

    // En mode onboarding, le candidat est déjà connecté : on saute le formulaire
    // d'identité (ask-informations / form-informations) et on bascule direct
    // sur final. applyPrefill() a déjà injecté les valeurs dans user_choices,
    // donc le payload reste cohérent côté back.
    if (this.mode === 'onboarding' && next.cle === 'ask-informations') {
      const finalNode = DIALOG.find((n) => n.cle === 'final');
      if (finalNode) next = finalNode;
    }

    if (next.cle.startsWith('final')) {
      this.currentData = next;
      this.waiting = true;
      this.btnLoading = true;
      this.emitStateChange();
      this.waitingMinPromise = new Promise((r) => setTimeout(r, WAITING_MIN_MS));
      let recap: SarahRecapApi | null = null;
      try {
        recap = await this.api.saveUserData(this.engine.buildUserDataPayload());
      } catch (e) {
        console.warn('[sarah-bot] saveUserData failed', e);
      } finally {
        this.btnLoading = false;
      }
      await this.waitingMinPromise;

      const choices = this.engine.buildUserDataPayload();
      this.emit('sarah:complete', { choices, recap });

      // Mode onboarding : pas de récap floutée — on reste sur le waiting
      // jusqu'à ce que l'app hôte cache le composant (via show_sarah=false
      // sur réception de sarah:complete). C'est plus naturel : le user
      // termine sur "Votre tableau de bord va s'ouvrir" puis y atterrit.
      if (this.mode === 'onboarding') return;

      this.finalRecap = recap;
      this.isFinal = true;
      this.waiting = false;
      return;
    }

    // Terminal pro (msg sans enfants)
    if (next.type === 'msg' && next.children && !next.children.choices.length && !next.children.form.length) {
      this.currentData = next;
      this.emitStateChange();
      this.proTerminalKey = next.cle;
      this.isFinal = true;
      return;
    }

    this.applyNode(next);
  }

  // ---------- Login flow ----------

  private async doLogin(email: string, password: string): Promise<void> {
    this.inscriptionError = '';
    try {
      const res = await this.api.doLogin(email, password);
      if (res && res.data && res.auth) {
        this.cookies.set('a-commonAccess', res.auth.accessToken ?? res.auth.commonAccess ?? '', 30, '/');
        this.cookies.set('a-userId', String(res.auth.userID ?? res.auth.userId ?? ''), 30, '/');
        if (res.auth.refreshToken || res.auth.refreshAccess) {
          this.cookies.set('a-refreshAccess', String(res.auth.refreshToken ?? res.auth.refreshAccess), 30, '/');
        }
        this.inscriptionConnected = true;
        this.emit('sarah:login-success', { auth: res.auth });
        if (this.mode === 'signup') {
          setTimeout(() => {
            window.location.href = this.appUrl;
          }, REDIRECT_AFTER_LOGIN_MS);
        }
        this.modalOpen = false;
      } else {
        this.inscriptionError = (res && res.message) || STRINGS.loginInvalidCredentials;
      }
    } catch (e) {
      console.warn('[sarah-bot] doLogin failed', e);
      this.inscriptionError = STRINGS.loginNetworkError;
    }
  }

  // ---------- Récap "built" depuis les cookies (situation + articles) ----------

  private buildRecap(): { situation: RecapSituationLine[]; articles: RecapArticle[] } {
    const map = this.cookies.getJSON<string[]>('user_map', []);
    const choices = this.cookies.getJSON<Record<string, UserChoiceValue>>('user_choices', {});
    const cles = new Set<string>();
    for (const k of map) {
      const v = choices[k];
      if (typeof v === 'string') cles.add(v);
    }
    const situation: RecapSituationLine[] = [];
    const articles: RecapArticle[] = [];
    cles.forEach((k) => {
      const entry: RecapEntry | undefined = RECAP.find((r) => r.key === k);
      if (entry) {
        entry.value.forEach((it) => situation.push(it));
        if (entry.articles) entry.articles.forEach((a) => articles.push(a));
      }
    });
    situation.sort((a, b) => (a.ordre || 0) - (b.ordre || 0));
    return { situation, articles };
  }

  // ---------- Rendu ----------

  /** Terminaux informationnels (rendus comme une conversation normale, pas en carte). */
  private static readonly INFO_TERMINALS = new Set(['i-no-pm', 'time-to-quit']);

  render() {
    if (this.proTerminalKey) {
      return SarahBot.INFO_TERMINALS.has(this.proTerminalKey)
        ? this.renderInfoTerminal()
        : this.renderProTerminal();
    }
    if (this.isFinal) return this.renderRecap();
    if (this.waiting) return this.renderWaiting();
    if (!this.currentData) return nothing;
    return this.renderConversation();
  }

  private renderInfoTerminal() {
    const cle = this.proTerminalKey!;
    const node = DIALOG.find((n) => n.cle === cle);
    const avatarSrc = `${this.assetsBase}/speaker-avatar.jpg`;
    const ctaLabel = this.mode === 'onboarding' ? 'Terminer' : "Retour à l'accueil";
    return html`
      <div id="sarah" @typing-done=${this.onTypingDone}>
        ${keyed(cle, html`
          <div class="sarah-container">
            <sarah-speaker
              .message=${node ? (node.message as string) : ''}
              avatar-src=${avatarSrc}
              ?instant=${this.engine.action.is === 'back'}
            ></sarah-speaker>
            <div class="reveal-wrap ${this.speakerDone ? 'after-typing' : 'before-typing'}">
              ${this.speakerDone ? html`
                <div class="button-container">
                  <button class="btn" type="button"
                    @click=${() => this.emit('sarah:exit', { reason: 'home' })}>
                    ${ctaLabel}
                  </button>
                </div>
              ` : nothing}
            </div>
          </div>
        `)}
      </div>
    `;
  }

  private renderConversation() {
    const node = this.currentData!;
    const children = node.children || { choices: [], form: [], msg: [] };
    const avatarSrc = `${this.assetsBase}/speaker-avatar.jpg`;
    const afterTyping = this.speakerDone ? 'after-typing' : 'before-typing';
    return html`
      <div
        id="sarah"
        @choice=${this.onChoice}
        @valid-change=${this.onFormValidChange}
        @action=${this.onNext}
        @open-login=${this.onOpenLogin}
        @typing-done=${this.onTypingDone}
      >
        ${keyed(node.cle, html`
          <div class="sarah-container">
            ${this.bonjourShouldShow
              ? html`<div class="speaker-bonjour">
                  <p>Bonjour et bienvenue, je m'appelle Sarah et je suis à votre disposition pour toute information sur le test psychotechnique.</p>
                </div>`
              : nothing}
            <sarah-speaker
              .message=${node.message as string}
              avatar-src=${avatarSrc}
              ?instant=${this.engine.action.is === 'back'}
            ></sarah-speaker>
            ${node.cle === 'ask-prefecture-medecin' && this.speakerDone
              ? html`<img
                  class="courrier-exemple reveal"
                  src=${`${this.assetsBase}/Pieces_jour_commission_medicale.png`}
                  alt="Exemple de courrier de la commission médicale"
                />`
              : nothing}
            <div class="reveal-wrap ${afterTyping}">
              ${this.speakerDone ? html`
                ${children.choices.length > 0
                  ? html`<sarah-choix
                      .choices=${children.choices}
                      saved-choice=${typeof this.savedCurrentData === 'string' ? this.savedCurrentData : ''}
                      assets-base=${this.assetsBase}
                    ></sarah-choix>`
                  : nothing}
                ${children.form.map((f) => html`
                  <sarah-formulaire
                    .formulaire=${f}
                    .savedForm=${this.currentFormContent ?? (typeof this.savedCurrentData === 'object' ? (this.savedCurrentData as FormContent) : null)}
                    ?exists=${this.existsEmail}
                    .departements=${DEPARTEMENTS}
                  ></sarah-formulaire>
                `)}
                ${children.form.length > 0
                  ? html`<sarah-bouton
                      label=${STRINGS.btnNext}
                      ?disabled=${this.isBtnDisabled || this.btnLoading}
                      ?loading=${this.btnLoading}
                    ></sarah-bouton>`
                  : nothing}
              ` : nothing}
            </div>
          </div>
        `)}
        ${this.renderModalIfOpen()}
      </div>
    `;
  }

  private renderWaiting() {
    // Priorise le prefill.prenom (cas onboarding où l'utilisateur n'a pas
    // saisi son prénom dans le formulaire) ; fallback sur le cookie qui est
    // peuplé par form-informations en mode signup.
    const cookiePrenom = this.cookies.get('user_visitor').replace(/^"|"$/g, '');
    const prenom = (this.prefill && this.prefill.prenom) || cookiePrenom || '';
    return html`
      <div id="sarah">
        <div class="sarah-waiting-wrap">
          <sarah-waiting prenom=${prenom} mode=${this.mode}></sarah-waiting>
        </div>
      </div>
    `;
  }

  private renderRecap() {
    const built = this.buildRecap();
    const prenom = this.cookies.get('user_visitor').replace(/^"|"$/g, '') || '';
    const finalNode = DIALOG.find((n) => n.cle === 'final');
    return html`
      <div
        id="sarah"
        class="sarah--final"
        @login-submit=${this.onLoginSubmit}
      >
        <sarah-recap
          .prenom=${prenom}
          .recapApi=${this.finalRecap}
          .recapBuilt=${built}
          assets-base=${this.assetsBase}
          final-message=${finalNode ? (finalNode.message as string) : ''}
          avatar-src=${`${this.assetsBase}/speaker-avatar.jpg`}
        ></sarah-recap>
      </div>
    `;
  }

  private renderProTerminal() {
    const cle = this.proTerminalKey!;
    return html`
      <div id="sarah" class="sarah--final">
        <div class="pro-terminal">
          ${this.renderProBody(cle)}
        </div>
      </div>
    `;
  }

  private renderProBody(cle: string) {
    if (cle === 'pro-form-done') {
      return html`
        <h2>${STRINGS.articlesTitle}</h2>
        <div class="articles">
          ${ARTICLES_PRO.p.map((a) => html`
            <a href=${a.article} target="_blank" rel="noopener" title=${a.titre}>
              <span>${a.titre}</span><span>→</span>
            </a>
          `)}
        </div>
        <p style="text-align:center;margin:20px 0;">ou</p>
        <div class="goto-pro">
          <a href="https://www.aaaep-professionnel.fr" target="_blank" rel="noopener"
             @click=${() => this.emit('sarah:exit', { reason: 'pro-site' })}>
            ${STRINGS.proAccessSite}
          </a>
        </div>
      `;
    }
    if (cle === 'pro-come-back') {
      return html`
        <h2>${STRINGS.articlesTitle}</h2>
        <div class="articles">
          ${ARTICLES_PRO.z.map((a) => html`
            <a href=${a.article} target="_blank" rel="noopener" title=${a.titre}>
              <span>${a.titre}</span><span>→</span>
            </a>
          `)}
        </div>
      `;
    }
    if (cle === 'pro-form-search-test' || cle === 'pro-b-ask-objectif') {
      return html`
        <h2>Rechercher un test psychotechnique</h2>
        <form class="search-form" @submit=${this.onSearchSubmit}>
          <select required>
            <option value="">${STRINGS.placeholderDepartement}</option>
            ${DEPARTEMENTS.map((d) => html`
              <option value=${`${d.num_departement}|${d.nom_departement}`}>
                ${d.full_departement}
              </option>
            `)}
          </select>
          <button type="submit" class="btn">${STRINGS.btnSearchTests}</button>
        </form>
      `;
    }
    // i-no-pm / time-to-quit ne passent plus ici — gérés par renderInfoTerminal.
    return nothing;
  }

  private onSearchSubmit = (e: Event): void => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const sel = form.querySelector('select') as HTMLSelectElement | null;
    if (!sel || !sel.value) return;
    const [num, nom] = sel.value.split('|');
    const slug = nom
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    window.location.href = `https://www.aaaep.fr/test-psychotechnique/${num}/${slug}`;
  };

  private renderModalIfOpen() {
    if (!this.modalOpen) return nothing;
    return html`
      <div class="modal-overlay" @click=${this.onModalBackdropClick} @keydown=${this.onModalKeydown}>
        <div
          class="modal-inner"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          @click=${(e: Event) => e.stopPropagation()}
        >
          <h2 id="modal-title" class="sr-only" style="position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);margin:-1px;padding:0;border:0;">
            ${STRINGS.inscriptionTitle}
          </h2>
          <button class="modal-close" @click=${this.onModalCloseClick} aria-label="Fermer">×</button>
          <sarah-inscription
            mode="modal"
            .connected=${this.inscriptionConnected}
            .errorMessage=${this.inscriptionError}
            @login-submit=${this.onLoginSubmit}
          ></sarah-inscription>
        </div>
      </div>
    `;
  }

  private onModalBackdropClick = (): void => {
    this.closeModal();
  };

  private onModalCloseClick = (): void => {
    this.closeModal();
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'sarah-bot': SarahBot;
  }
}

